import {
  Button,
  Collapsible,
  CollapsibleRoot,
  LoadingSpinner,
  SheetContentWrapper, space,
  SpaceBetween,
  tk, tok, Typography,
} from '@sunwebgroup/ui-components';
import * as React from 'react';
import { styled } from 'styled-components';
import { useService } from '@/scopes/checkout/domain/hooks';
import { ServiceType } from '@/scopes/checkout/domain/types';
import { useGetAllCarsQuery } from '@/scopes/checkout/filterableCars/domain/hooks';
import { CarView } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/CarView';
import {
  DriverAgeFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/DriverAgeFilter';
import {
  PickUpLocationFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/PickUpLocationFilter';
import {
  AdditionalServicesFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/AdditionalServicesFilter';
import {
  TransmissionFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/TransmissionFilter';
import {
  CarClassFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/CarClassFilter';
import {
  FuelTypeFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/FuelTypeFilter';
import {
  DepositTypeFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/DepositTypeFilter';
import {
  ChildSeatsFilter
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/ChildSeatsFilter';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { useIntl } from '@/intl';
import {
  selectedFiltersSelector,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors';
import { toSnakeCase } from '@/helpers/string-helpers';

export function FilterableCarsAvailable(){
  const {service} = useService(ServiceType.CarRentalFiltering);
  const { isLoading, data } = useGetAllCarsQuery();
  const { filters, cars } = data ?? {};
  const { isDesktopMd } = useBreakpoints();
  const showFiltersByDefault = isDesktopMd;
  const [showFilters, setShowFilters] = React.useState(showFiltersByDefault);
  const [showCars, setShowCars] = React.useState(true);
  const contentWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const intl = useIntl();

  function handleFiltersOpen() {
    setShowFilters(!showFilters);
    setShowCars(showFilters);
  }

  function handleCarsOpen() {
    setShowCars(!showCars);
    setShowFilters(showCars);
  }

  React.useEffect(() => {
    const checkSheetWidth = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth >= 960) {
        setShowFilters(true);
        setShowCars(true);
      } else {
        setShowFilters(false);
        setShowCars(true);
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      checkSheetWidth();
    });

    if (contentWrapperRef.current) {
      resizeObserver.observe(contentWrapperRef.current);
    }

    window.addEventListener('resize', checkSheetWidth);

    return () => {
      window.removeEventListener('resize', checkSheetWidth);

      if (contentWrapperRef.current) {
        resizeObserver.unobserve(contentWrapperRef.current);
      }
    };
  }, []);

  if (!service) return null;

  const carsLength = Object.keys(cars ?? {}).length;
  const selectedFilters = selectedFiltersSelector(filters);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <CarsFilterWrapper ref={contentWrapperRef} >
          { filters &&
              <FiltersSection>
                {!showFiltersByDefault && !showFilters && <ShowFiltersTriggerSection>
                  {selectedFilters && (
                    <SelectedFiltersSummary>
                      {Object.entries(selectedFilters).flatMap(([key, values]) =>
                          values.map(value => (
                            intl.formatMessage({
                              id: `checkout_steps_ancillaries_car_rental_filtering_filters_${toSnakeCase(key.toLowerCase())}_type_${toSnakeCase(value)}_text`,
                              defaultMessage: value,
                            })
                          )).join(', ')
                      ).join(', ')}
                    </SelectedFiltersSummary>
                  )}
                  <Button $hierarchy="primary" $size='small' onClick={handleFiltersOpen}>
                    {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_filters_show_all_cars_change_filters_button'})}
                  </Button>
                </ShowFiltersTriggerSection>
                }
                <CollapsibleRoot open={showFilters} onOpenChange={handleFiltersOpen}>
                  <Collapsible>
                    <Filters>
                      <SpaceBetween direction='y' spacing={space('primitives_space_12')}>
                        <DriverAgeFilter />
                        <PickUpLocationFilter />
                        <AdditionalServicesFilter />
                        <TransmissionFilter />
                        <CarClassFilter />
                        <FuelTypeFilter />
                        <DepositTypeFilter />
                        <ChildSeatsFilter />
                      </SpaceBetween>
                      {!showFiltersByDefault &&
                        <FiltersBottomSection>
                          <Button $hierarchy="primary" onClick={handleCarsOpen} $fullSize>
                            {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_filters_show_all_cars_button'}, {amount: carsLength})}
                          </Button>
                        </FiltersBottomSection>
                      }
                    </Filters>
                  </Collapsible>
                </CollapsibleRoot>
              </FiltersSection>
          }
          <CarsSection>
            { cars && <CollapsibleRoot open={showCars} onOpenChange={handleCarsOpen}>
              <Collapsible>
                <Cars>
                  <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
                    { Object.values(cars ?? {})
                      .map((car) => (
                        <CarView key={car.id} {...car} />
                      ))
                    }
                  </SpaceBetween>
                </Cars>

              </Collapsible>
            </CollapsibleRoot> }
          </CarsSection>
        </CarsFilterWrapper>
      )}
    </>
  )
}
const ShowFiltersTriggerSection = styled.div`
  padding: ${space('primitives_space_16')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${tok('semantics_backgrounds_background_color')};
  border-bottom: 1px solid ${tok('semantics_dividers_color')};
`
const SelectedFiltersSummary = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${tok('primitives_font_size_body_m')}px;
  font-weight: 600;
  line-height: 125%;
  color: ${tok('semantics_buttons_tinted_neutral_content_color')};
`;
const Filters = styled.div`
  padding: ${space('primitives_space_16')};
  padding-bottom: ${space('primitives_space_64')};
  @media ${tk('mq_desktop_md')} {
    padding-bottom: ${space('primitives_space_16')};
  }
`
const Cars = styled.div`
  padding-bottom: 50px;
  @media ${tk('mq_desktop_md')} {
    padding-bottom: ${space('primitives_space_0')};
  }
`
const Loader = styled(LoadingSpinner)`
  height: calc(100% - 50px);
  margin: auto;
`
const FiltersSection = styled.div`
  width: 100%;
  background-color: ${tok('semantics_backgrounds_background_color')};

  @media ${tk('mq_desktop_md')} {
    overflow-y: scroll;
  }
`
const FiltersBottomSection = styled.div`
  padding: ${space('primitives_space_16')};
  background-color: ${tok('semantics_backgrounds_background_color')};
  border-top: 1px solid ${tok('semantics_dividers_color')};
  position: fixed;
  bottom: 0;
  width: 100%;
  left: 0;
}
`
const CarsSection = styled.div`
  width: 100%;
  padding: ${space('primitives_space_16')};

  @media ${tk('mq_desktop_md')} {
    overflow-y: scroll;
  }
`
const CarsFilterWrapper = styled(SheetContentWrapper)`
  padding: 0;

  @media ${tk('mq_desktop_md')} {
    display: grid;
    grid-template-columns: 320px 1fr;
    grid-template-rows: 1fr;
    overflow: hidden;
  }

  @media ${tk('mq_desktop_lg')} {
    display: grid;
    grid-template-columns: 375px 1fr;
    grid-template-rows: 1fr;
  }

`
